// v10/src/components/admin/AdminPanel.js - ROLE-BASED ACCESS
// v2.0 — Sidebar: sección "Mi cuenta" colapsable + persistencia de preferencia
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plane, ClipboardCheck, LogOut, Moon, Sun, Clock,
  Menu, X, Shield, ChevronRight, Home, Images, Lock,
  Volume2, ShieldCheck, LayoutDashboard, HelpCircle,
  AlertTriangle, ShieldAlert, PackageOpen, BookOpen, CreditCard, Calendar,
  Workflow, Siren, UserCircle, Wrench, Info,
  BarChart3, TrendingUp, Building2, Settings, Hourglass, Wallet, ScanLine,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AdminUsers       from './AdminUsers';
import AdminAircraft    from './AdminAircraft';
import AdminInspections from './AdminInspections';
import AdminImages      from './AdminImages';
import AdminAudioCache  from './AdminAudioCache';
import AdminDashboard   from './AdminDashboard';
import AuditLog         from '../AuditLog';
import AdminHobbsReport from './AdminHobbsReport';
import AdminFlightLogImport from './AdminFlightLogImport';
import AdminHourPackages from './AdminHourPackages';
import AdminOpenFlights from './AdminOpenFlights';
import AdminSquawks     from './AdminSquawks';
import AdminFlightAuth  from './AdminFlightAuth';
import AdminSuppliers      from './AdminSuppliers';
import AdminSubscription  from './AdminSubscription';
import AdminTurnos        from './AdminTurnos';
import FlowStats          from './FlowStats';
import FlowTrapConfig     from './FlowTrapConfig';
import EmergencyStats     from './EmergencyStats';
import EmergencyProcedureManager from './EmergencyProcedureManager';
import MaintenanceInventoryScreen from './MaintenanceInventoryScreen';
import TourGuide          from '../TourGuide';
import NotificationManager from '../NotificationManager';
import api from '../../services/apiService';
import { reportsService } from '../../services/reportsService';
import { POLLING_INTERVALS } from '../../config/realtime';
import useIsMobile from '../../hooks/useIsMobile';

const ROLE_DOT_COLORS = {
  admin:      '#4f46e5',
  instructor: '#3b82f6',
  pilot:      '#22c55e',
};

const ROLE_TEXT_COLORS = {
  admin:      'text-indigo-600',
  instructor: 'text-blue-500',
  pilot:      'text-green-500',
};

const ROLE_BG = {
  admin:      'bg-indigo-50',
  instructor: 'bg-blue-100',
  pilot:      'bg-green-100',
};

const RoleBadge = ({ role, darkMode }) => {
  const { t } = useTranslation('admin');
  const dot  = ROLE_DOT_COLORS[role]  || ROLE_DOT_COLORS.pilot;
  const text = ROLE_TEXT_COLORS[role] || ROLE_TEXT_COLORS.pilot;
  const bg   = ROLE_BG[role]          || ROLE_BG.pilot;
  const label = role === 'admin' ? t('role_admin_label')
              : role === 'instructor' ? t('role_instructor_label')
              : t('role_pilot_label');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: darkMode ? 'rgba(255,255,255,0.07)' : undefined }}
      className={!darkMode ? bg : ''}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <span className={text}>{label}</span>
    </span>
  );
};

const LockedTab = ({ item }) => (
  <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '16px', opacity: 0.55, cursor: 'not-allowed' }}>
    <item.icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
    <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>{item.label}</span>
    <Lock style={{ width: '13px', height: '13px', opacity: 0.6 }} />
  </div>
);

const AdminPanel = ({ user, userRole, onLogout, darkMode, onToggleDark, onGoHome, impersonatedOrgId, impersonatedOrgName, orgFeatures, isAuthenticated, onShowProfile, onAbout }) => {
  const { t, i18n } = useTranslation('admin');
  const role     = userRole || user?.role || 'pilot';
  const features = orgFeatures || user?.organization_features || {};
  const isMobile = useIsMobile();
  // Ribbon "Modo demo" (DemoBanner, App.js) es fixed/top en mobile — reserva
  // espacio arriba para no tapar el header móvil (hamburguesa/logo/badges).
  const demoTopOffset = (isMobile && user?.is_demo) ? 52 : 0;

  const handleDownloadManual = async () => {
    try {
      const lang = (i18n?.language || 'es').startsWith('en') ? 'en' : 'es';
      await reportsService.manual(lang);
    } catch (err) {
      console.error('Error descargando el manual:', err);
      alert(t('manual_download_error', { defaultValue: 'No se pudo descargar el manual. Reintentá en unos segundos.' }));
    }
  };

  // ── Grupos del sidebar ────────────────────────────────────────────
  const NAV_GROUPS = [
    { id: 'operation',  label: t('group_operation'),  icon: BarChart3,  defaultCollapsed: false },
    { id: 'resources',  label: t('group_resources'),  icon: Plane,      defaultCollapsed: false },
    { id: 'reports',    label: t('group_reports'),    icon: TrendingUp, defaultCollapsed: false },
    { id: 'config',     label: t('group_config'),     icon: Settings,   defaultCollapsed: true  },
    { id: 'enterprise', label: t('group_enterprise'), icon: Building2,  defaultCollapsed: true  },
  ];

  // ── Tabs — built using translations ──────────────────────────────
  const BASE_NAV_ITEMS = [
    { id: 'dashboard',    groupId: 'operation',  label: t('tab_dashboard'),         icon: LayoutDashboard, roles: ['admin', 'instructor'], description: t('tab_dashboard_desc'),    color: '#3b82f6' },
    { id: 'flightauth',   groupId: 'operation',  label: t('tab_flightauth'),        icon: ShieldAlert,     roles: ['admin', 'instructor'], description: t('tab_flightauth_desc'),   color: '#ef4444' },
    { id: 'inspections',  groupId: 'operation',  label: t('tab_inspections'),       icon: ClipboardCheck,  roles: ['admin', 'instructor'], description: t('tab_inspections_desc'),  color: '#22c55e' },
    { id: 'squawks',      groupId: 'operation',  label: t('tab_squawks'),           icon: AlertTriangle,   roles: ['admin', 'instructor'], description: t('tab_squawks_desc'),      color: '#f59e0b' },
    { id: 'open-flights', groupId: 'operation',  label: t('tab_open_flights'),      icon: Hourglass,       roles: ['admin', 'instructor'], description: t('tab_open_flights_desc'), color: '#f59e0b' },
    { id: 'turnos',       groupId: 'operation',  label: t('tab_turnos'),            icon: Calendar,        roles: ['admin', 'instructor'], description: t('tab_turnos_desc'),       color: '#8b5cf6' },
    { id: 'hour-packages', groupId: 'operation', label: 'Paquetes de horas',        icon: Wallet,          roles: ['admin', 'instructor'], description: 'Saldo de horas por alumno (paquetes)', color: '#16a34a' },
    { id: 'aircraft',     groupId: 'resources',  label: t('tab_aircraft'),          icon: Plane,           roles: ['admin', 'instructor'], description: t('tab_aircraft_desc'),     color: '#06b6d4' },
    { id: 'users',        groupId: 'resources',  label: t('tab_users'),             icon: Users,           roles: ['admin'],               description: t('tab_users_desc'),        color: '#8b5cf6' },
    { id: 'images',       groupId: 'config',     label: t('tab_aircraft_gallery'),  icon: Images,          roles: ['admin', 'instructor'], description: t('tab_images_desc'),       color: '#ec4899' },
    { id: 'audio',        groupId: 'config',     label: t('tab_voice_cache'),       icon: Volume2,         roles: ['admin', 'instructor'], description: t('tab_audio_desc'),        color: '#14b8a6' },
    { id: 'report',       groupId: 'reports',    label: t('tab_report'),            icon: Clock,           roles: ['admin', 'instructor'], description: t('tab_report_desc'),       color: '#f97316' },
    { id: 'flight-log-import', groupId: 'reports', label: 'Importar planillas',     icon: ScanLine,        roles: ['admin'],               description: 'Digitalizar planillas históricas con Vision', color: '#7c3aed' },
    { id: 'suppliers',    groupId: 'resources',  label: t('tab_suppliers'),         icon: PackageOpen,     roles: ['admin', 'instructor'], description: t('tab_suppliers_desc'),    color: '#64748b', flag: 'suppliers' },
    { id: 'maintenance',  groupId: 'config',     label: t('tab_maintenance'),       icon: Wrench,          roles: ['admin', 'instructor'], description: t('tab_maintenance_desc'),  color: '#4f46e5' },
    { id: 'flowmode',     groupId: 'enterprise', label: t('tab_flowmode'),          icon: Workflow,        roles: ['admin', 'instructor'], description: t('tab_flowmode_desc'),     color: '#818cf8', flag: 'flow_mode' },
    { id: 'emergency',    groupId: 'enterprise', label: t('tab_emergency'),         icon: Siren,           roles: ['admin', 'instructor'], description: t('tab_emergency_desc'),    color: '#ef4444', flag: 'emergency_trainer' },
    { id: 'subscription', groupId: 'config',     label: t('tab_billing'),           icon: CreditCard,      roles: ['admin'],               description: t('tab_subscription_desc'), color: '#16a34a' },
    { id: 'audit',        groupId: 'reports',    label: t('tab_audit_history'),     icon: ShieldCheck,     roles: ['admin'],               description: t('tab_audit_desc'),        color: '#6d28d9' },
  ];

  const NAV_ITEMS   = BASE_NAV_ITEMS.filter(n => !n.flag || features[n.flag] === true);
  // superadmin ve todos los tabs que ve admin
  const effectiveRole = role === 'superadmin' ? 'admin' : role;
  const allowedTabs = NAV_ITEMS.filter(n => n.roles.includes(effectiveRole));
  const lockedTabs  = NAV_ITEMS.filter(n => !n.roles.includes(effectiveRole));

  const [activeTab, setActiveTab] = useState(() => {
    const saved    = localStorage.getItem('condor360_admin_tab');
    const validTab = allowedTabs.find(t => t.id === saved);
    if (saved && !validTab) localStorage.removeItem('condor360_admin_tab');
    return validTab ? saved : (allowedTabs[0]?.id || 'dashboard');
  });

  const [sidebarOpen, setSidebarOpen]           = useState(false);
  const [navCollapsed, setNavCollapsed]         = useState(() => localStorage.getItem('condor360_nav_collapsed') === '1');
  const [accountCollapsed, setAccountCollapsed] = useState(() => localStorage.getItem('condor360_account_collapsed') === '1');
  const [collapsedGroups, setCollapsedGroups]   = useState(() => {
    const initial = {};
    NAV_GROUPS.forEach(g => { if (g.defaultCollapsed) initial[g.id] = true; });
    return initial;
  });
  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };
  const [guideKey, setGuideKey]                 = useState(0);
  const [showTour, setShowTour]                 = useState(false);
  const [pendingAuthCount, setPendingAuthCount] = useState(0);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await api.get('/api/flight-auth/pending');
        setPendingAuthCount(res.data.count || 0);
      } catch (_) {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, POLLING_INTERVALS.flight_auth); 
    return () => clearInterval(interval);
  }, []);

  // Persistencia de los toggles de colapso del sidebar
  useEffect(() => { localStorage.setItem('condor360_nav_collapsed', navCollapsed ? '1' : '0'); }, [navCollapsed]);
  useEffect(() => { localStorage.setItem('condor360_account_collapsed', accountCollapsed ? '1' : '0'); }, [accountCollapsed]);

  const ADMIN_TOUR_STEPS = [
    { id: 'welcome',      title: t('tour_welcome_title'),      content: t('tour_welcome_content'),      selector: null, position: 'center' },
    { id: 'dashboard',    title: t('tour_dashboard_title'),    content: t('tour_dashboard_content'),    selector: null, position: 'center' },
    { id: 'flightauth',   title: t('tour_flightauth_title'),   content: t('tour_flightauth_content'),   selector: null, position: 'center' },
    { id: 'inspections',  title: t('tour_inspections_title'),  content: t('tour_inspections_content'),  selector: null, position: 'center' },
    { id: 'squawks',      title: t('tour_squawks_title'),      content: t('tour_squawks_content'),      selector: null, position: 'center' },
    { id: 'turnos',       title: t('tour_turnos_title'),       content: t('tour_turnos_content'),       selector: null, position: 'center' },
    { id: 'aircraft',     title: t('tour_aircraft_title'),     content: t('tour_aircraft_content'),     selector: null, position: 'center' },
    { id: 'users',        title: t('tour_users_title'),        content: t('tour_users_content'),        selector: null, position: 'center' },
    { id: 'report',       title: t('tour_report_title'),       content: t('tour_report_content'),       selector: null, position: 'center' },
    { id: 'flowmode',     title: t('tour_flowmode_title'),     content: t('tour_flowmode_content'),     selector: null, position: 'center' },
    { id: 'emergency',    title: t('tour_emergency_title'),    content: t('tour_emergency_content'),    selector: null, position: 'center' },
    { id: 'subscription', title: t('tour_subscription_title'), content: t('tour_subscription_content'), selector: null, position: 'center' },
    { id: 'done',         title: t('tour_done_title'),         content: t('tour_done_content'),         selector: null, position: 'center' },
  ];

  const roleInfo = { dot: ROLE_DOT_COLORS[role] || ROLE_DOT_COLORS.pilot };

  const handleTabChange = (tabId) => {
    localStorage.setItem('condor360_admin_tab', tabId);
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  const handleShowGuide = () => {
    setSidebarOpen(false);
    setTimeout(() => setShowTour(true), 300);
  };
  const handleProfileClick = () => { setSidebarOpen(false); if (onShowProfile) onShowProfile(); };
  const handleAboutClick = () => { setSidebarOpen(false); if (onAbout) onAbout(); };

  const getAccessDesc = () => {
    if (role === 'admin')      return t('access_admin');
    if (role === 'instructor') return t('access_instructor');
    return t('access_pilot');
  };

  const renderContent = () => {
    if (!allowedTabs.find(tab => tab.id === activeTab)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px', opacity: 0.5 }}>
          <Lock style={{ width: '48px', height: '48px' }} />
          <p style={{ fontSize: '16px', fontWeight: 700 }}>{t('no_access_section')}</p>
        </div>
      );
    }
    switch (activeTab) {
      case 'dashboard':    return <AdminDashboard   darkMode={darkMode} guideKey={guideKey} />;
      case 'flightauth':   return <AdminFlightAuth   darkMode={darkMode} userRole={role} />;
      case 'inspections':  return <AdminInspections  darkMode={darkMode} userRole={role} currentUser={user} />;
      case 'squawks':      return <AdminSquawks      darkMode={darkMode} userRole={role} />;
      case 'open-flights': return <AdminOpenFlights  darkMode={darkMode} />;
      case 'aircraft':     return <AdminAircraft     darkMode={darkMode} userRole={role} orgFeatures={features} onNavigateTo={handleTabChange} />;
      case 'users':        return <AdminUsers        currentUser={user} darkMode={darkMode} />;
      case 'images':       return <AdminImages       darkMode={darkMode} />;
      case 'audio':        return <AdminAudioCache   darkMode={darkMode} currentUser={user} />;
      case 'report':       return <AdminHobbsReport  darkMode={darkMode} user={user} />;
      case 'flight-log-import': return <AdminFlightLogImport darkMode={darkMode} />;
      case 'suppliers':    return <AdminSuppliers    darkMode={darkMode} userRole={role} />;
      case 'maintenance':  return <MaintenanceInventoryScreen darkMode={darkMode} userRole={role} />;
      case 'turnos':       return <AdminTurnos       darkMode={darkMode} userRole={role} userId={user?.id} />;
      case 'hour-packages': return <AdminHourPackages darkMode={darkMode} userRole={role} />;
      case 'subscription': return <AdminSubscription darkMode={darkMode} orgFeatures={features} />;
      case 'audit':        return <AuditLog          darkMode={darkMode} />;
      case 'flowmode':     return (
        <div>
          <FlowStats darkMode={darkMode} />
          <div style={{ marginTop: '40px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, paddingTop: '32px' }}>
            <FlowTrapConfig darkMode={darkMode} />
          </div>
        </div>
      );
      case 'emergency':    return (
        <div>
          <EmergencyStats darkMode={darkMode} currentUser={user} />
          <div style={{ marginTop: '40px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, paddingTop: '32px' }}>
            <EmergencyProcedureManager darkMode={darkMode} />
          </div>
        </div>
      );
      default: return null;
    }
  };

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <div className={`px-6 py-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '14px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(37,99,235,0.35)' }}>
            <Shield style={{ width: '20px', height: '20px', color: 'white' }} />
          </div>
          <div>
            <p className={`font-black text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>Condor360</p>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('control_panel')}</p>
          </div>
        </div>
      </div>

      {/* User */}
      <div className={`px-4 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className={`flex items-center gap-3 p-3 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
          <div style={{ width: '36px', height: '36px', borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '16px', background: `${roleInfo.dot}20`, color: roleInfo.dot }}>
            {user?.name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className={`font-semibold text-sm truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user?.name}</p>
            <div style={{ marginTop: '3px' }}><RoleBadge role={role} darkMode={darkMode} /></div>
          </div>
        </div>
        <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '12px', background: darkMode ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.12)' }}>
          <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: darkMode ? 'rgba(147,197,253,0.8)' : '#1e40af', lineHeight: 1.5 }}>
            {getAccessDesc()}
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
        <button onClick={() => setNavCollapsed(p => !p)}
          style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', margin:'4px 0 8px', padding:'4px 8px', background:'none', border:'none', cursor:'pointer', borderRadius:8 }}>
          <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)' }}>
            {t('sections')}
          </p>
          <ChevronRight style={{ width:12, height:12, color: darkMode?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.3)', transform: navCollapsed ? 'rotate(90deg)' : 'rotate(270deg)', transition:'transform 0.2s' }} />
        </button>
        {!navCollapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {NAV_GROUPS.map(group => {
              const groupItems = allowedTabs.filter(item => item.groupId === group.id);
              if (groupItems.length === 0) return null;
              const isCollapsed = collapsedGroups[group.id];
              const GroupIcon = group.icon;
              return (
                <div key={group.id}>
                  <button onClick={() => toggleGroup(group.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer',
                      borderRadius: 8,
                      opacity: 0.8,
                      marginBottom: '4px',
                      fontFamily: 'inherit'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <GroupIcon style={{ width: 12, height: 12, color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', flexShrink: 0 }} />
                    <p style={{
                      margin: 0,
                      flex: 1,
                      textAlign: 'left',
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)'
                    }}>
                      {group.label}
                    </p>
                    <ChevronRight style={{
                      width: 12, height: 12,
                      color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)',
                      transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                      transition: 'transform 0.2s'
                    }} />
                  </button>
                  {!isCollapsed && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {groupItems.map(item => {
                        const Icon      = item.icon;
                        const isActive  = activeTab === item.id;
                        const showBadge = item.id === 'flightauth' && pendingAuthCount > 0;
                        return (
                          <motion.button key={item.id}
                            data-tour={`admin-nav-${item.id}`}
                            onClick={() => handleTabChange(item.id)}
                            whileHover={{ x: isActive ? 0 : 4 }} whileTap={{ scale: 0.98 }}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '11px 14px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                              fontFamily: 'inherit', fontSize: '14px', fontWeight: 600, textAlign: 'left',
                              transition: 'all 0.15s',
                              background: isActive ? `linear-gradient(135deg,${item.color},${item.color}dd)` : 'transparent',
                              color: isActive ? 'white' : darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)',
                              boxShadow: isActive ? `0 4px 14px ${item.color}40` : 'none',
                            }}>
                            <Icon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <span>{item.label}</span>
                              {isActive && <p style={{ margin: '2px 0 0', fontSize: '10px', opacity: 0.75, fontWeight: 400 }}>{item.description}</p>}
                            </div>
                            {showBadge && !isActive && (
                              <motion.span
                                animate={{ scale:[1,1.15,1] }} transition={{ duration:1.5, repeat:Infinity }}
                                style={{ minWidth:'20px', height:'20px', borderRadius:'10px', background:'#ef4444', color:'white', fontSize:'10px', fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px', flexShrink:0 }}>
                                {pendingAuthCount}
                              </motion.span>
                            )}
                            {isActive && <ChevronRight style={{ width: '14px', height: '14px', opacity: 0.6 }} />}
                          </motion.button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {lockedTabs.length > 0 && (
          <>
            <p style={{ margin: '16px 8px 8px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)' }}>
              {t('no_access')}
            </p>
            {lockedTabs.map(item => <LockedTab key={item.id} item={item} darkMode={darkMode} />)}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className={`px-3 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>

        {/* Header colapsable "Mi cuenta" */}
        <button onClick={() => setAccountCollapsed(p => !p)}
          style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', margin:'0 0 6px', padding:'4px 8px', background:'none', border:'none', cursor:'pointer', borderRadius:8 }}>
          <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)' }}>
            {t('my_account', { defaultValue: 'Mi cuenta' })}
          </p>
          <ChevronRight style={{ width:12, height:12, color: darkMode?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.3)', transform: accountCollapsed ? 'rotate(90deg)' : 'rotate(270deg)', transition:'transform 0.2s' }} />
        </button>

        {!accountCollapsed && <>
          {/* a) Bienvenido al Panel Admin */}
          <button onClick={handleShowGuide}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.07)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <HelpCircle style={{ width: '17px', height: '17px', flexShrink: 0, color: '#8b5cf6' }} />
            <span style={{ color: '#8b5cf6', fontWeight: 600 }}>👋 {t('welcome_panel', { defaultValue: 'Bienvenido al Panel Admin' })}</span>
          </button>

          {/* b) Manual de uso */}
          <button onClick={handleDownloadManual}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.07)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <BookOpen style={{ width: '17px', height: '17px', flexShrink: 0, color: '#2563eb' }} />
            <span style={{ color: '#2563eb', fontWeight: 600 }}>{t('manual_pdf', { defaultValue: 'Manual de uso (PDF)' })}</span>
          </button>

          {/* c) Selector de aeronaves */}
          {onGoHome && (
            <button onClick={onGoHome}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <Home style={{ width: '17px', height: '17px', flexShrink: 0 }} />
              <span>{t('aircraft_selector')}</span>
            </button>
          )}

          {/* d) Modo oscuro */}
          <button onClick={onToggleDark}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {darkMode ? <Sun style={{ width: '17px', height: '17px', flexShrink: 0 }} /> : <Moon style={{ width: '17px', height: '17px', flexShrink: 0 }} />}
            <span>{darkMode ? t('light_mode') : t('dark_mode')}</span>
          </button>

          {/* e) Mi perfil */}
          {onShowProfile && (
            <button onClick={handleProfileClick}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <UserCircle style={{ width: '17px', height: '17px', flexShrink: 0 }} />
              <span>{t('my_profile', { defaultValue: 'Mi perfil' })}</span>
            </button>
          )}

          {/* f) Acerca de */}
          {onAbout && (
            <button onClick={handleAboutClick}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <Info style={{ width: '17px', height: '17px', flexShrink: 0 }} />
              <span>{t('about_menu_item', { defaultValue: 'Acerca de' })}</span>
            </button>
          )}
        </>}

        {/* f) Cerrar sesión — siempre visible (acción crítica) */}
        <button onClick={onLogout}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'transparent', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', color: '#ef4444', transition: 'background 0.15s', marginTop: accountCollapsed ? 0 : '4px' }}
          onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.06)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <LogOut style={{ width: '17px', height: '17px', flexShrink: 0 }} />
          <span>{t('close_session')}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className={`flex h-screen overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
        style={{ paddingTop: demoTopOffset, boxSizing: 'border-box' }}>
        {/* Sidebar desktop */}
        <aside className={`hidden lg:flex flex-col w-64 flex-shrink-0 border-r ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <SidebarContent />
        </aside>

        {/* Sidebar móvil */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
                className="lg:hidden" onClick={() => setSidebarOpen(false)} />
              <motion.aside initial={{ x: -288 }} animate={{ x: 0 }} exit={{ x: -288 }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className={`fixed inset-y-0 left-0 z-50 w-72 flex flex-col border-r shadow-2xl lg:hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button onClick={() => setSidebarOpen(false)}
                  style={{ position: 'absolute', top: '12px', right: '12px', padding: '8px', borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'transparent' }}>
                  <X style={{ width: '20px', height: '20px' }} />
                </button>
                <SidebarContent />
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Header móvil */}
          <header className={`lg:hidden flex items-center gap-2 sm:gap-4 px-4 py-3 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <button onClick={() => setSidebarOpen(true)} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <Menu style={{ width: '20px', height: '20px' }} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '10px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Shield style={{ width: '14px', height: '14px', color: 'white' }} />
              </div>
              <span className={`font-bold whitespace-nowrap text-sm sm:text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('control_panel')}</span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <RoleBadge role={role} darkMode={darkMode} />
              {pendingAuthCount > 0 && (
                <motion.button onClick={() => handleTabChange('flightauth')}
                  animate={{ scale:[1,1.08,1] }} transition={{ duration:1.5, repeat:Infinity }}
                  style={{ padding:'5px 10px', borderRadius:'20px', background:'rgba(239,68,68,0.1)', border:'1.5px solid rgba(239,68,68,0.3)', fontSize:'11px', fontWeight:800, color:'#ef4444', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px' }}>
                  <ShieldAlert style={{ width:'12px', height:'12px' }} />
                  {pendingAuthCount}
                </motion.button>
              )}
              <button onClick={handleDownloadManual}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.07)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, color: '#2563eb' }}>
                <BookOpen style={{ width: '14px', height: '14px' }} />
              </button>
              <button onClick={() => { setSidebarOpen(false); window.dispatchEvent(new CustomEvent('condor360:open-assistant')); }}
                title={t('view_guide', { defaultValue: 'Ver guía de uso' })}
                style={{ padding: '6px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: darkMode ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)', color: '#8b5cf6', display: 'flex', alignItems: 'center' }}>
                <HelpCircle style={{ width: '16px', height: '16px' }} />
              </button>
            </div>
          </header>

          <main style={{ flex: 1, overflowY: 'auto' }} className="p-6 pb-24 lg:pb-6">
            <AnimatePresence mode="wait">
              <motion.div key={activeTab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.2 }}>
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      <TourGuide
        active={showTour}
        onDone={() => { setShowTour(false); localStorage.setItem('condor360_admin_tour_done', '1'); }}
        darkMode={darkMode}
        steps={ADMIN_TOUR_STEPS}
      />

      <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 50 }}>
        <NotificationManager darkMode={darkMode} isAuthenticated={isAuthenticated ?? true} />
      </div>
    </>
  );
};

export default AdminPanel;
